import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { pauseCampaignResponseSchema } from '@borradh-workspace/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { ListCampaignsResponse } from '../types';

/**
 * Pause campaign hook
 * Pauses a running campaign on Meta Ads
 */
export const usePauseCampaign = (
  toastOnSuccess = true,
  toastOnError = true
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (campaignId: string) => {
      return apiClient.post<{ paused: true }>(
        `meta-campaigns/${campaignId}/pause`,
        undefined,
        { schema: pauseCampaignResponseSchema }
      );
    },
    onMutate: async (campaignId) => {
      const campaignsKey = queryKeys.metaAds.campaigns();
      await queryClient.cancelQueries({ queryKey: campaignsKey });
      const previous = queryClient.getQueriesData<ListCampaignsResponse>({
        queryKey: campaignsKey,
      });
      queryClient.setQueriesData<ListCampaignsResponse>(
        { queryKey: campaignsKey },
        (old) => {
          if (!old?.campaigns) return old;
          return {
            ...old,
            campaigns: old.campaigns.map((c) =>
              c.id === campaignId
                ? { ...c, status: 'PAUSED', effectiveStatus: 'PAUSED' }
                : c
            ),
          };
        }
      );
      return { previous };
    },
    onSuccess: (_, campaignId) => {
      void queryClient.invalidateQueries({ queryKey: ['meta-campaigns'] });
      void queryClient.invalidateQueries({
        queryKey: ['meta-campaigns', campaignId],
      });
      if (toastOnSuccess) toast.success('Campaign paused');
    },
    onError: (error, _campaignId, context) => {
      if (context?.previous) {
        for (const [key, data] of context.previous) {
          queryClient.setQueryData(key, data);
        }
      }
      if (toastOnError)
        toast.error(`Failed to pause campaign: ${error.message}`);
    },
  });

  return {
    ...mutation,
    execute: mutation.mutate,
    executeAsync: mutation.mutateAsync,
    isExecuting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
